import type { SignerConfig, SignerVaultInfo } from '../types/signers';
import { emblemPost } from './http';
import { fetchVaultInfo } from './vault';

/**
 * Bitcoin address types
 */
export type BitcoinAddressType = 'p2pkh' | 'p2wpkh' | 'p2tr' | 'p2sh';

/**
 * Bitcoin network type
 */
export type BitcoinNetwork = 'mainnet' | 'testnet';

/**
 * Input signing instruction for PSBTs
 * Compatible with UniSat wallet-api's UserToSignInput format
 */
export interface BitcoinToSignInput {
  /** Input index in the PSBT */
  index: number;
  /** Bitcoin address associated with this input */
  address?: string;
  /** Public key (hex) for this input */
  publicKey?: string;
  /** Sighash types for signing */
  sighashTypes?: number[];
  /** Tap leaf hash to sign (hex) for Taproot script path spending */
  tapLeafHashToSign?: string;
  /** Use tweaked signer for Taproot */
  useTweakedSigner?: boolean;
  /** Disable tweak signer for Taproot */
  disableTweakSigner?: boolean;
}

/**
 * Bitcoin sign transaction options
 */
export interface BitcoinSignOptions {
  /** Transaction type determines signing algorithm (ECDSA for p2pkh/p2wpkh, Schnorr for p2tr) */
  transactionType: 'p2pkh' | 'p2wpkh' | 'p2tr';
  /** Per-input signing instructions for complex PSBTs */
  toSignInputs?: BitcoinToSignInput[];
}

/**
 * Response from signing a Bitcoin PSBT
 */
export interface BitcoinSignResult {
  success: boolean;
  /** Signed PSBT in base64 format */
  signedPsbt: string;
  /** Final transaction hex (ready for broadcast) */
  signedTxHex: string;
  /** Raw signature */
  signature: string;
}

/**
 * Extended vault info including Bitcoin public key and derived addresses
 */
export interface BitcoinVaultInfo extends SignerVaultInfo {
  /** Bitcoin public key (compressed, hex) */
  btcPubkey: string;
  /** Derived Bitcoin addresses */
  btcAddresses?: {
    /** Legacy (1...) */
    p2pkh: string;
    /** Native SegWit (bc1q...) */
    p2wpkh: string;
    /** Taproot (bc1p...) */
    p2tr: string;
  };
}

/**
 * Bitcoin signer interface compatible with common Bitcoin libraries
 */
export interface BitcoinSignerInterface {
  /** Bitcoin public key (compressed, hex) */
  publicKey: string;
  /** Derived Bitcoin addresses */
  addresses: {
    p2pkh: string;
    p2wpkh: string;
    p2tr: string;
  };
  /** Sign a PSBT */
  signPsbt(psbtBase64: string, options: BitcoinSignOptions): Promise<BitcoinSignResult>;
  /** Sign multiple PSBTs */
  signAllPsbts(
    psbts: Array<{ psbt: string; options: BitcoinSignOptions }>
  ): Promise<BitcoinSignResult[]>;
  /** Get the vault ID */
  getVaultId(): string;
  /** Get public key as hex */
  getPublicKey(): string;
}

/**
 * Constants for Bitcoin calculations
 */
export const SATS_PER_BTC = 100_000_000;

/**
 * Emblem Bitcoin Signer implementation
 */
export class EmblemBitcoinSigner implements BitcoinSignerInterface {
  readonly publicKey: string; // Compressed public key (hex)
  readonly addresses: {
    p2pkh: string;
    p2wpkh: string;
    p2tr: string;
  };
  private readonly config: SignerConfig;
  private readonly vaultId: string;

  constructor(config: SignerConfig, vaultInfo: BitcoinVaultInfo) {
    if (!vaultInfo.btcPubkey) {
      throw new Error('Bitcoin public key is required in vault info');
    }
    this.publicKey = vaultInfo.btcPubkey;
    this.addresses = vaultInfo.btcAddresses ?? {
      p2pkh: '',
      p2wpkh: '',
      p2tr: '',
    };
    this.config = config;
    this.vaultId = vaultInfo.vaultId;
  }

  /**
   * Sign a Bitcoin PSBT
   * @param psbtBase64 - PSBT in base64 format
   * @param options - Signing options including transaction type and per-input instructions
   */
  async signPsbt(psbtBase64: string, options: BitcoinSignOptions): Promise<BitcoinSignResult> {
    const { transactionType, toSignInputs } = options;

    // Normalize toSignInputs for JSON transmission
    const normalizedInputs = toSignInputs?.map(input => ({
      ...input,
      // Ensure tapLeafHashToSign is a string (some libs pass Buffer/Uint8Array)
      tapLeafHashToSign: this.normalizeToHex(input.tapLeafHashToSign),
    }));

    const response = await emblemPost<BitcoinSignResult>(
      '/sign-btc-transaction',
      {
        vaultId: this.vaultId,
        psbt: psbtBase64,
        transactionType,
        toSignInputs: normalizedInputs,
      },
      this.config
    );

    return response;
  }

  /**
   * Sign multiple PSBTs
   * @param psbts - Array of PSBTs with their signing options
   */
  async signAllPsbts(
    psbts: Array<{ psbt: string; options: BitcoinSignOptions }>
  ): Promise<BitcoinSignResult[]> {
    const results: BitcoinSignResult[] = [];
    for (const { psbt, options } of psbts) {
      results.push(await this.signPsbt(psbt, options));
    }
    return results;
  }

  /**
   * Get the vault ID
   */
  getVaultId(): string {
    return this.vaultId;
  }

  /**
   * Get public key as hex
   */
  getPublicKey(): string {
    return this.publicKey;
  }

  /**
   * Check if a given address belongs to this signer
   */
  ownsAddress(address: string): boolean {
    return (
      address === this.addresses.p2pkh ||
      address === this.addresses.p2wpkh ||
      address === this.addresses.p2tr
    );
  }

  /**
   * Get the appropriate address for a given type
   */
  getAddress(type: BitcoinAddressType = 'p2wpkh'): string {
    switch (type) {
      case 'p2pkh':
        return this.addresses.p2pkh;
      case 'p2wpkh':
        return this.addresses.p2wpkh;
      case 'p2tr':
        return this.addresses.p2tr;
      case 'p2sh':
        // P2SH is typically wrapped SegWit, return p2wpkh as fallback
        return this.addresses.p2wpkh;
      default:
        return this.addresses.p2wpkh;
    }
  }

  /**
   * Normalize a value to hex string (handles string, Buffer, Uint8Array)
   */
  private normalizeToHex(value: string | ArrayLike<number> | undefined): string | undefined {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string') return value;
    // Handle Buffer/Uint8Array
    const bytes = value as ArrayLike<number>;
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
      hex += (bytes[i] as number).toString(16).padStart(2, '0');
    }
    return hex;
  }
}

/**
 * Create an Emblem Bitcoin signer from config
 * @param config - Signer configuration with authentication
 * @param infoOverride - Optional vault info to skip API call
 */
export async function toBitcoinSigner(
  config: SignerConfig,
  infoOverride?: BitcoinVaultInfo
): Promise<EmblemBitcoinSigner> {
  const info = infoOverride ?? (await fetchBitcoinVaultInfo(config));
  return new EmblemBitcoinSigner(config, info);
}

/**
 * Fetch vault info with Bitcoin-specific fields
 */
export async function fetchBitcoinVaultInfo(config: SignerConfig): Promise<BitcoinVaultInfo> {
  const data = await emblemPost<{
    vaultId: string;
    address?: string;
    evmAddress?: string;
    btcPubkey?: string;
    btcAddresses?: {
      p2pkh: string;
      p2wpkh: string;
      p2tr: string;
    };
    created_by?: string;
  }>('/vault/info', {}, config);

  if (!data || !data.vaultId) {
    throw new Error('Invalid vault info response: missing vaultId');
  }

  if (!data.btcPubkey) {
    throw new Error('Invalid vault info response: missing btcPubkey (Bitcoin public key)');
  }

  return {
    vaultId: data.vaultId,
    address: data.address || '',
    evmAddress: (data.evmAddress || '0x') as `0x${string}`,
    btcPubkey: data.btcPubkey,
    btcAddresses: data.btcAddresses,
    created_by: data.created_by,
  };
}

/**
 * Utility: Detect Bitcoin address type from address string
 */
export function detectAddressType(address: string): BitcoinAddressType | 'unknown' {
  if (address.startsWith('bc1p')) return 'p2tr'; // Taproot (bech32m)
  if (address.startsWith('bc1q')) return 'p2wpkh'; // Native SegWit (bech32)
  if (address.startsWith('1')) return 'p2pkh'; // Legacy
  if (address.startsWith('3')) return 'p2sh'; // Script Hash
  // Testnet addresses
  if (address.startsWith('tb1p')) return 'p2tr';
  if (address.startsWith('tb1q')) return 'p2wpkh';
  if (address.startsWith('m') || address.startsWith('n')) return 'p2pkh';
  if (address.startsWith('2')) return 'p2sh';
  return 'unknown';
}

/**
 * Utility: Convert satoshis to BTC
 */
export function satsToBTC(sats: number): number {
  return sats / SATS_PER_BTC;
}

/**
 * Utility: Convert BTC to satoshis
 */
export function btcToSats(btc: number): number {
  return Math.round(btc * SATS_PER_BTC);
}

/**
 * Utility: Format satoshis for display
 */
export function formatSats(sats: number): string {
  return `${sats.toLocaleString()} sats (${satsToBTC(sats).toFixed(8)} BTC)`;
}

/**
 * Utility: Estimate transaction size in vbytes
 */
export function estimateTransactionSize(
  inputCount: number,
  outputCount: number,
  inputType: BitcoinAddressType = 'p2wpkh'
): number {
  let inputSize: number;
  switch (inputType) {
    case 'p2pkh':
      inputSize = 148; // Legacy (no witness discount)
      break;
    case 'p2tr':
      inputSize = 58; // Taproot (approx vbytes)
      break;
    case 'p2wpkh':
    default:
      inputSize = 68; // Native SegWit (approx vbytes)
      break;
  }

  const outputSize = 31; // Bech32 output (approx vbytes)
  const overhead = 10; // Version, locktime, etc.

  return overhead + inputCount * inputSize + outputCount * outputSize;
}

/**
 * Utility: Calculate estimated fee
 */
export function calculateFee(vsize: number, feeRate: number): number {
  return Math.ceil(vsize * feeRate);
}

/**
 * Utility: Check if amount is above dust threshold
 */
export function isDust(sats: number, addressType: BitcoinAddressType = 'p2wpkh'): boolean {
  // Dust threshold: ~546 sats for legacy, ~294 for SegWit
  const dustThreshold = addressType === 'p2wpkh' || addressType === 'p2tr' ? 294 : 546;
  return sats < dustThreshold;
}
